import React, { useState, useEffect } from "react";
import InputMask from "react-input-mask";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";
import EventApi from "../../services/event";
import { formatDocument } from "./index";

// ── Constants ─────────────────────────────────────────────────────────────────

const EVENT_CATEGORIES = ["Participante", "Patrocinador", "Organização", "Painelista", "Staff"];
const GENDERS = ["Masculino", "Feminino", "Prefiro não informar"];
const SECTORS = ["Público", "Privado"];

const AREA_OPTIONS = {
    Público: ["Setor Aeroportuário", "Outros setores da economia"],
    Privado: [
        "Setor Aeroportuário",
        "Outros setores da economia",
        "Escritório de Advocacia",
        "Consultor(a)",
        "Entidades Associativas e Sindicais",
        "Staff",
        "Outros",
    ],
};

// ── EditDialog ────────────────────────────────────────────────────────────────

const EditDialog = ({ open, person, onClose, onSave }) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // Preenche o formulário sempre que a pessoa muda
    useEffect(() => {
        if (person) {
            setFormData({
                name: person.name || "",
                name_tag: person.name_tag || "",
                email: person.email || "",
                phone: person.phone || "",
                company: person.company || "",
                role: person.role || "",
                event_category: person.event_category || "",
                gender: person.gender || "",
                extras: person.extras || "",
                custom_fields: person.custom_fields || "",
            });
            setErrors({});
        }
    }, [person]);

    // ── Change handler ─────────────────────────────────────────────────────

    function onChange(e) {
        const { name, value } = e.target;
        setFormData((prev) => {
            const updated = { ...prev, [name]: value };

            // Lógica condicional Setor → Área
            if (name === "extras") {
                const newAreaOptions = AREA_OPTIONS[value] || [];
                if (prev.custom_fields && !newAreaOptions.includes(prev.custom_fields)) {
                    updated.custom_fields = "";
                }
            }

            return updated;
        });

        // Limpa erro do campo ao editar
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    }

    // ── Validation ─────────────────────────────────────────────────────────

    function validate() {
        const newErrors = {};

        if (!formData.name || formData.name.trim().length < 3)
            newErrors.name = "Nome completo inválido (mínimo 3 caracteres)";
        if (!formData.name_tag || !formData.name_tag.trim())
            newErrors.name_tag = "Nome do crachá é obrigatório";
        if (!formData.email || !formData.email.trim())
            newErrors.email = "E-mail é obrigatório";
        if (!formData.company || !formData.company.trim())
            newErrors.company = "Empresa é obrigatória";
        if (!formData.role || !formData.role.trim())
            newErrors.role = "Cargo é obrigatório";
        if (!formData.event_category)
            newErrors.event_category = "Selecione uma categoria";
        if (!formData.gender)
            newErrors.gender = "Selecione um gênero";
        if (!formData.extras)
            newErrors.extras = "Selecione um setor";
        if (!formData.custom_fields)
            newErrors.custom_fields = "Selecione uma área";

        return newErrors;
    }

    // ── Submit ─────────────────────────────────────────────────────────────

    async function handleSave() {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const payload = {
                // Todos os dados da pessoa preservados
                ...person,
                // Campos editáveis atualizados
                name: formData.name.trim(),
                name_tag: formData.name_tag.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone ? formData.phone.replace(/\D/g, "") : person.phone,
                company: formData.company.trim(),
                role: formData.role.trim(),
                event_category: formData.event_category,
                gender: formData.gender,
                extras: formData.extras,
                custom_fields: formData.custom_fields,
                // Campos críticos preservados explicitamente
                solicitation_approved: person.solicitation_approved,
                approved_at: person.approved_at,
                cpf: person.cpf,
                address: person.address,
            };

            const resp = await EventApi.updatePerson(person.id, payload);

            if (resp && resp.data) {
                onSave();
            } else {
                toast.error("Erro ao atualizar dados. Tente novamente");
            }
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error("Erro ao atualizar dados. Tente novamente");
        } finally {
            setLoading(false);
        }
    }

    function handleCancel() {
        setErrors({});
        onClose();
    }

    // ── Helpers ────────────────────────────────────────────────────────────

    const areaOptions = formData.extras ? (AREA_OPTIONS[formData.extras] || []) : [];

    // ── Render ─────────────────────────────────────────────────────────────

    if (!person) return null;

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
            <DialogTitle>Editar Inscrição</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>

                    {/* Campos somente leitura */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Campos não editáveis
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="CPF / Passaporte"
                            value={formatDocument(person.cpf)}
                            disabled
                            fullWidth
                            variant="outlined"
                            size="small"
                            helperText="Não editável"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Estado"
                            value={person.address || "-"}
                            disabled
                            fullWidth
                            variant="outlined"
                            size="small"
                            helperText="Não editável"
                        />
                    </Grid>

                    {/* Divisor */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom style={{ marginTop: 8 }}>
                            Dados editáveis
                        </Typography>
                    </Grid>

                    {/* Nome completo */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="edit-name"
                            name="name"
                            label="Nome Completo *"
                            value={formData.name || ""}
                            onChange={onChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.name}
                            helperText={errors.name}
                            disabled={loading}
                        />
                    </Grid>

                    {/* Nome do crachá */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="edit-name-tag"
                            name="name_tag"
                            label="Nome do Crachá *"
                            value={formData.name_tag || ""}
                            onChange={onChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.name_tag}
                            helperText={errors.name_tag}
                            disabled={loading}
                        />
                    </Grid>

                    {/* E-mail */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="edit-email"
                            name="email"
                            label="E-mail *"
                            type="email"
                            value={formData.email || ""}
                            onChange={onChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={loading}
                        />
                    </Grid>

                    {/* Telefone com máscara */}
                    <Grid item xs={12} sm={6}>
                        <InputMask
                            mask="+55 (99) 9 9999-9999"
                            value={formData.phone || ""}
                            onChange={onChange}
                            maskChar=""
                            disabled={loading}
                        >
                            {() => (
                                <TextField
                                    id="edit-phone"
                                    name="phone"
                                    label="Telefone"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    disabled={loading}
                                />
                            )}
                        </InputMask>
                    </Grid>

                    {/* Empresa */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="edit-company"
                            name="company"
                            label="Empresa / Instituição *"
                            value={formData.company || ""}
                            onChange={onChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.company}
                            helperText={errors.company}
                            disabled={loading}
                        />
                    </Grid>

                    {/* Cargo */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="edit-role"
                            name="role"
                            label="Cargo *"
                            value={formData.role || ""}
                            onChange={onChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.role}
                            helperText={errors.role}
                            disabled={loading}
                        />
                    </Grid>

                    {/* Categoria */}
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.event_category}
                            disabled={loading}
                        >
                            <InputLabel id="edit-category-label">Categoria *</InputLabel>
                            <Select
                                labelId="edit-category-label"
                                id="edit-event-category"
                                name="event_category"
                                value={formData.event_category || ""}
                                onChange={onChange}
                                label="Categoria *"
                            >
                                {EVENT_CATEGORIES.map((cat) => (
                                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                ))}
                            </Select>
                            {errors.event_category && (
                                <FormHelperText>{errors.event_category}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Gênero */}
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.gender}
                            disabled={loading}
                        >
                            <InputLabel id="edit-gender-label">Gênero *</InputLabel>
                            <Select
                                labelId="edit-gender-label"
                                id="edit-gender"
                                name="gender"
                                value={formData.gender || ""}
                                onChange={onChange}
                                label="Gênero *"
                            >
                                {GENDERS.map((g) => (
                                    <MenuItem key={g} value={g}>{g}</MenuItem>
                                ))}
                            </Select>
                            {errors.gender && (
                                <FormHelperText>{errors.gender}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Setor */}
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.extras}
                            disabled={loading}
                        >
                            <InputLabel id="edit-extras-label">Setor *</InputLabel>
                            <Select
                                labelId="edit-extras-label"
                                id="edit-extras"
                                name="extras"
                                value={formData.extras || ""}
                                onChange={onChange}
                                label="Setor *"
                            >
                                {SECTORS.map((s) => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </Select>
                            {errors.extras && (
                                <FormHelperText>{errors.extras}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Área — condicional ao Setor */}
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.custom_fields}
                            disabled={loading || !formData.extras}
                        >
                            <InputLabel id="edit-area-label">Área *</InputLabel>
                            <Select
                                labelId="edit-area-label"
                                id="edit-custom-fields"
                                name="custom_fields"
                                value={formData.custom_fields || ""}
                                onChange={onChange}
                                label="Área *"
                            >
                                {areaOptions.length === 0 ? (
                                    <MenuItem value="" disabled>
                                        Selecione primeiro o Setor
                                    </MenuItem>
                                ) : (
                                    areaOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))
                                )}
                            </Select>
                            {errors.custom_fields && (
                                <FormHelperText>{errors.custom_fields}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleCancel} color="default" disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? "Salvando..." : "Salvar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditDialog;
